import type { ArbitrumVerdict } from "../arbitrum/types";
import type { RiskFlowAlert } from "../../lib/riskflow-feed";
import type { DayPlan, DayPlanWindow } from "../../types/day-plan";
import type {
  TiltScoutEvidence,
  TiltScoutInstrumentOutlook,
  TiltScoutProof,
} from "./tilt-scout-types";
import { headlineWeightForAlert } from "./tilt-scout-headline-weight";

const STALE_TAPE_PATTERN = /\btwo[\s-]+way\s+tape\b/gi;

export function normalizeTiltScoutText(value?: string | null, fallback = "") {
  const trimmed = value?.trim();
  if (!trimmed) return fallback;
  return trimmed.replace(STALE_TAPE_PATTERN, "balanced tape");
}

export function normalizeTiltScoutReadLabel(label?: string | null) {
  const trimmed = normalizeTiltScoutText(label).trim();
  if (!trimmed) return "Balanced";
  const normalized = trimmed
    .replace(/[–—-]/g, " ")
    .replace(/\s+/g, " ")
    .toLowerCase();
  if (normalized === "balanced tape") return "Balanced";
  return trimmed;
}

export function toEvidence(alert?: RiskFlowAlert): TiltScoutEvidence | null {
  if (!alert) return null;
  const weight = headlineWeightForAlert(alert);
  return {
    id: alert.id,
    title: alert.headline,
    source: alert.source,
    score: weight.headlineIvScore ?? 0,
    headlineWeight: weight.headlineWeight,
    headlineIvScore: weight.headlineIvScore,
    headlineEventWeight: weight.headlineEventWeight,
    headlineFreshness: weight.headlineFreshness,
    headlineImpactWeight: weight.headlineImpactWeight,
    headlineWeightSource: weight.headlineWeightSource,
    direction: alert.direction ?? "Neutral",
    publishedAt: alert.publishedAt,
  };
}

export function findActiveWindow(windows: DayPlanWindow[]) {
  if (windows.length === 0) return null;
  const now = currentEasternMinutes();
  return (
    windows.find((window) => {
      const start = minutesFromClock(window.startTime);
      const end = minutesFromClock(window.endTime);
      return start != null && end != null && now >= start && now <= end;
    }) ?? windows[0]
  );
}

export function rangeText(
  outlook: TiltScoutInstrumentOutlook | null,
  evidence: TiltScoutEvidence | null,
) {
  if (Array.isArray(outlook?.range) && outlook.range.length >= 2) {
    return `${formatPoints(Number(outlook.range[0]))} to ${formatPoints(Number(outlook.range[1]))} pts`;
  }
  if (evidence?.score) return `IV ${evidence.score.toFixed(1)} context`;
  return "Range pending";
}

export function invalidationText(
  window: DayPlanWindow | null,
  evidence: TiltScoutEvidence | null,
) {
  const invalidation = window?.econForecast?.whatInvalidates;
  if (invalidation) return limit(invalidation, 82);
  if (evidence)
    return limit(
      `Fade if ${normalizeTiltScoutText(evidence.title)} loses confirmation.`,
      82,
    );
  return "Invalidation pending";
}

export function eventText(
  window: DayPlanWindow | null,
  dayPlan: DayPlan | null,
) {
  return normalizeTiltScoutText(
    window?.eventName ??
      dayPlan?.eventName ??
      dayPlan?.deskTheme ??
      "Session tape",
  );
}

export function buildProofs({
  instrument,
  topEvidence,
  verdict,
  dayPlan,
}: {
  instrument: TiltScoutInstrumentOutlook | null;
  topEvidence: TiltScoutEvidence | null;
  verdict: ArbitrumVerdict | null;
  dayPlan: DayPlan | null;
}): TiltScoutProof[] {
  return [
    {
      label: "Brief",
      status: dayPlan ? "live" : "partial",
      detail: normalizeTiltScoutText(eventText(null, dayPlan)),
    },
    {
      label: "RiskFlow",
      status: topEvidence ? "live" : "partial",
      detail: normalizeTiltScoutText(topEvidence?.title, "No catalyst"),
    },
    {
      label: "Chart",
      status: instrument ? "live" : "partial",
      detail: rangeText(instrument, topEvidence),
    },
    {
      label: "Chamber",
      status: verdict ? "live" : "degraded",
      detail: verdict?.category ?? "No verdict",
    },
    { label: "Scout", status: "queued", detail: "Proof context" },
  ];
}

export function confidenceText(
  instrument: TiltScoutInstrumentOutlook | null,
  evidence: TiltScoutEvidence | null,
  verdict: ArbitrumVerdict | null,
  dayPlan: DayPlan | null,
) {
  const count = [instrument, evidence, verdict, dayPlan].filter(Boolean).length;
  if (count >= 4) return "triangulated";
  if (count >= 2) return "paired";
  return "partial";
}

export function scoreLabel(score: number) {
  if (score >= 6) return "Conviction long";
  if (score >= 2) return "Bullish pressure";
  if (score <= -6) return "Conviction short";
  if (score <= -2) return "Bearish pressure";
  return "Balanced";
}

function currentEasternMinutes() {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date());
  const hour = Number(parts.find((part) => part.type === "hour")?.value ?? 0);
  const minute = Number(
    parts.find((part) => part.type === "minute")?.value ?? 0,
  );
  return hour * 60 + minute;
}

function minutesFromClock(value: string) {
  const match = value.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;
  return Number(match[1]) * 60 + Number(match[2]);
}

function formatPoints(value: number) {
  if (!Number.isFinite(value)) return "0";
  return `${value > 0 ? "+" : ""}${Math.round(value)}`;
}

function limit(value: string, max: number) {
  if (value.length <= max) return value;
  return `${value.slice(0, max - 3).trimEnd()}...`;
}
